import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Search, X, Copy, Check, Package, Loader2, ImageOff } from "lucide-react";

/**
 * GlobalArticleSearch
 * Floating bottom-right search button. Click → opens panel.
 * Bidirectional lookup: type a code → see size/description, OR type a
 * description / size / EAN → see the matching code(s).
 * Backed by GET /api/article-data/search/articles?q=...
 * Thumbnails resolved via POST /api/article-images/batch-thumbnails.
 */

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

export default function GlobalArticleSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const [copiedKey, setCopiedKey] = useState("");
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input on open
  useEffect(() => {
    if (open) {
      // small delay so the panel mounts first
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setQ("");
      setResults([]);
      setThumbs({});
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/article-data/search/articles`, {
          params: { q: q.trim(), limit: 25 },
        });
        const arts = r.data.articles || [];
        setResults(arts);
        // Fetch thumbs for codes that don't already carry a photo_url
        const codes = arts
          .map((a) => a.article_no || a.sf_code)
          .filter(Boolean);
        if (codes.length) {
          try {
            const t = await axios.post(`${API}/article-images/batch-thumbnails`, {
              article_nos: codes,
            });
            setThumbs(t.data || {});
          } catch {
            setThumbs({});
          }
        }
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const copyValue = useCallback(async (value, key) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1200);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <>
      {/* Floating launcher button — icon-only, same size as GlobalCalculator,
          sits to its left so the two coexist. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="global-article-search-button"
          className="fixed bottom-5 right-20 z-40 h-12 w-12 rounded-full
                     bg-gradient-to-br from-indigo-600 to-violet-600 text-white
                     shadow-lg hover:shadow-xl hover:scale-105 transition
                     flex items-center justify-center"
          aria-label="Search article database"
          title="Search article database"
        >
          <Search className="w-5 h-5" />
        </button>
      )}

      {open && (
        <div
          data-testid="global-article-search-panel"
          className="fixed bottom-4 right-4 z-[70] w-[min(92vw,440px)]
                     max-h-[min(80vh,720px)] rounded-2xl bg-white shadow-2xl
                     ring-1 ring-slate-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">
              Article Database
            </span>
            <span className="ml-auto text-[10px] opacity-80">
              code ↔ size / description
            </span>
            <button
              onClick={() => setOpen(false)}
              data-testid="global-article-search-close"
              className="ml-2 p-1 rounded hover:bg-white/15"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Input */}
          <div className="p-3 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Article No / SF Code / Description / Size / EAN"
                data-testid="global-article-search-input"
                className="w-full pl-9 pr-9 h-10 rounded-lg bg-white border border-slate-200
                           text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300
                           placeholder:text-slate-400"
              />
              {loading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 animate-spin" />
              )}
              {!loading && q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                  aria-label="Clear"
                  data-testid="global-article-search-clear"
                >
                  <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              Type at least 2 characters. Click any field below to copy.
            </p>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {q.trim().length < 2 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                Start typing to search the article database…
              </div>
            ) : !loading && results.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                No match for <span className="font-mono">&quot;{q}&quot;</span>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {results.map((a, idx) => {
                  const code = a.article_no || a.sf_code || "";
                  const thumb = thumbs[a.article_no] || thumbs[a.sf_code] || a.photo_url || "";
                  const baseKey = `${code}-${idx}`;
                  return (
                    <li
                      key={baseKey}
                      className="p-3 flex gap-3 hover:bg-indigo-50/50"
                      data-testid={`global-article-search-result-${idx}`}
                    >
                      {/* Thumbnail */}
                      <div className="w-16 h-16 shrink-0 rounded-lg bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={code}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <ImageOff className="w-5 h-5 text-slate-300" />
                        )}
                      </div>

                      {/* Fields */}
                      <div className="flex-1 min-w-0 text-xs space-y-0.5">
                        <KV label="Article No" value={a.article_no} mono accent
                            onCopy={(v) => copyValue(v, `${baseKey}-art`)}
                            copied={copiedKey === `${baseKey}-art`} />
                        {a.sf_code && a.sf_code !== a.article_no && (
                          <KV label="SF Code" value={a.sf_code} mono
                              onCopy={(v) => copyValue(v, `${baseKey}-sf`)}
                              copied={copiedKey === `${baseKey}-sf`} />
                        )}
                        <KV label="Size" value={a.size}
                            onCopy={(v) => copyValue(v, `${baseKey}-sz`)}
                            copied={copiedKey === `${baseKey}-sz`} />
                        <KV label="Description" value={a.article_des}
                            onCopy={(v) => copyValue(v, `${baseKey}-ds`)}
                            copied={copiedKey === `${baseKey}-ds`} />
                        {a.item_group && (
                          <KV label="Group" value={a.item_group}
                              onCopy={(v) => copyValue(v, `${baseKey}-gp`)}
                              copied={copiedKey === `${baseKey}-gp`} />
                        )}
                        {a.color && (
                          <KV label="Color" value={a.color}
                              onCopy={(v) => copyValue(v, `${baseKey}-co`)}
                              copied={copiedKey === `${baseKey}-co`} />
                        )}
                        {(a.pc_ean || a.ean_no) && (
                          <KV label="Pc EAN" value={a.pc_ean || a.ean_no} mono
                              onCopy={(v) => copyValue(v, `${baseKey}-pe`)}
                              copied={copiedKey === `${baseKey}-pe`} />
                        )}
                        {a.box_ean && (
                          <KV label="Box EAN" value={a.box_ean} mono
                              onCopy={(v) => copyValue(v, `${baseKey}-be`)}
                              copied={copiedKey === `${baseKey}-be`} />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function KV({ label, value, mono, accent, onCopy, copied }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-2 group">
      <span className="w-[78px] shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span
        className={`flex-1 break-words ${mono ? "font-mono" : ""} ${
          accent ? "text-indigo-700 font-semibold" : "text-slate-700"
        }`}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-indigo-100"
        title="Copy"
      >
        {copied ? (
          <Check className="w-3 h-3 text-emerald-600" />
        ) : (
          <Copy className="w-3 h-3 text-slate-400" />
        )}
      </button>
    </div>
  );
}
